import { useEffect } from "react";

/**
 * useAutoPlayVideo — bulletproof background-video autoplay.
 *
 * Attaches to a ref'd <video> element and aggressively re-attempts play() on:
 *   • ready events (canplay, loadeddata, loadedmetadata)
 *   • window load
 *   • visibility change (tab return)
 *   • window focus / pageshow (bfcache restore, minimize return)
 *   • First user gesture on the document (pointerdown/touchstart/keydown/scroll/click)
 *   • Video element being paused for any reason (e.g. user click) — re-play immediately
 *
 * Also blocks the built-in `click/pause` behavior so tapping the section
 * cannot pause the video.
 *
 * Respects `prefers-reduced-motion` — if enabled, does nothing.
 */
export default function useAutoPlayVideo(videoRef, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const el = videoRef.current;
    if (!el) return;

    // Enforce muted so autoplay policies always allow us
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute("muted", "");
    el.playsInline = true;
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");
    el.disablePictureInPicture = true;
    // Prevent context menu / native pause UI on right-click
    el.controls = false;

    let cancelled = false;

    const tryPlay = () => {
      if (cancelled || !el) return;
      // If already playing, nothing to do
      if (!el.paused && !el.ended) return;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    // Auto-resume if the video ever gets paused (e.g. user click, focus loss)
    const onPause = () => {
      if (el.ended) return;
      // Small delay so we don't fight a genuine internal pause during seeking
      setTimeout(tryPlay, 60);
    };

    // Kick off immediately + repeatedly for the first 3 seconds
    tryPlay();
    const burst = [50, 200, 500, 1000, 2000, 3000].map((ms) =>
      setTimeout(tryPlay, ms)
    );

    // Media events
    el.addEventListener("loadedmetadata", tryPlay);
    el.addEventListener("loadeddata", tryPlay);
    el.addEventListener("canplay", tryPlay);
    el.addEventListener("canplaythrough", tryPlay);
    el.addEventListener("pause", onPause);
    // Suspend/stalled can indicate paused download — retry when possible
    el.addEventListener("suspend", tryPlay);
    el.addEventListener("stalled", tryPlay);

    // Window/document events
    const onVisibility = () => {
      if (document.visibilityState === "visible") tryPlay();
    };
    const onFocus = tryPlay;
    const onPageShow = tryPlay;
    const onLoad = tryPlay;

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("load", onLoad);

    // First user-gesture fallback (autoplay was blocked)
    const gestureEvents = [
      "pointerdown",
      "touchstart",
      "click",
      "keydown",
      "scroll",
    ];
    const onGesture = () => {
      tryPlay();
      // Keep the scroll listener alive but drop discrete-gesture ones after first fire
      gestureEvents.forEach((evt) =>
        document.removeEventListener(evt, onGesture, { capture: true })
      );
    };
    gestureEvents.forEach((evt) =>
      document.addEventListener(evt, onGesture, {
        capture: true,
        passive: true,
      })
    );

    // Block user-initiated pause via clicking the <video> itself
    const stopClick = (e) => {
      // Don't preventDefault globally — just re-play if any pause slipped through.
      tryPlay();
    };
    el.addEventListener("click", stopClick);
    el.addEventListener("touchend", stopClick);

    return () => {
      cancelled = true;
      burst.forEach(clearTimeout);
      el.removeEventListener("loadedmetadata", tryPlay);
      el.removeEventListener("loadeddata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
      el.removeEventListener("canplaythrough", tryPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("suspend", tryPlay);
      el.removeEventListener("stalled", tryPlay);
      el.removeEventListener("click", stopClick);
      el.removeEventListener("touchend", stopClick);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("load", onLoad);
      gestureEvents.forEach((evt) =>
        document.removeEventListener(evt, onGesture, { capture: true })
      );
    };
  }, [videoRef, enabled]);
}
